import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import ClubLifeHomeSection from "../common/ClubLifeHomeSection";
import { staffJournalUrl, staffScheduleUrl } from "../../../utils/staffNav";
import { hubClientsPath } from "../../../utils/hubPaths";
import s from "../clinic.module.css";
import { SkeletonCard, useToast } from "../../ui";
import { IconCalendar, IconJournal, IconUsers } from "../../ui/icons";

function KpiCard({ label, value, hint, icon: Icon, accent }) {
  return (
    <article className={accent ? `${s.summaryKpi} ${s.summaryKpiAccent}` : s.summaryKpi}>
      {Icon ? <Icon className={s.summaryKpiIcon} width={18} height={18} /> : null}
      <span className={s.summaryKpiValue}>{value}</span>
      <span className={s.summaryKpiLabel}>{label}</span>
      {hint ? <span className={s.summaryKpiHint}>{hint}</span> : null}
    </article>
  );
}

function sessionProgress(present, expected) {
  if (!expected) return 0;
  return Math.min(100, Math.round((present / expected) * 100));
}

export default function StaffOverview({ appMode = false }) {
  const navigate = useNavigate();
  const toast = useToast();
  const today = toDateInputValue();
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clinicAPI.getSportToday();
      setDash(data);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useAdminRefresh(load);

  const sessionsToday = dash?.sessions || [];
  const nearestList =
    dash?.nearest_sessions?.length > 0
      ? dash.nearest_sessions
      : dash?.next_session
        ? [dash.next_session]
        : [];
  const expectedToday = dash?.expected_clients_today ?? 0;
  const markedToday = sessionsToday.reduce(
    (sum, sess) => sum + (sess.present_count ?? 0),
    0,
  );
  const notifications = (dash?.notifications || []).filter(
    (n) => !["debtors", "unpaid_membership"].includes(n.type),
  );

  const openJournal = (sess) => {
    if (!sess) {
      navigate(staffJournalUrl({ date: today }));
      return;
    }
    navigate(
      staffJournalUrl({
        date: sess.session_date || today,
        sessionId: sess.id,
        groupId: sess.group?.id,
      }),
    );
  };

  if (loading) {
    return (
      <div className={s.summaryPage}>
        <div className={s.summaryKpiStrip}>
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <SkeletonCard />
      </div>
    );
  }

  const todayLabel = formatDateRu(`${today}T12:00:00`, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className={s.summaryPage}>
      {!appMode ? (
        <header className={s.summaryDesktopHead}>
          <h1>Сводка</h1>
          <p>{todayLabel}</p>
        </header>
      ) : null}

      <section className={s.summaryKpiStrip} aria-label="Показатели дня">
        <KpiCard
          accent
          icon={IconUsers}
          label="Ожидается"
          value={expectedToday}
          hint={
            markedToday > 0
              ? `${markedToday} уже отмечено`
              : "на ваших занятиях"
          }
        />
        <KpiCard
          icon={IconCalendar}
          label="Занятий сегодня"
          value={sessionsToday.length}
          hint={
            nearestList[0]
              ? `ближайшее в ${formatTimeRu(nearestList[0].start_datetime)}`
              : "ваше расписание"
          }
        />
      </section>

      {notifications.length > 0 ? (
        <section className={s.summaryBlock}>
          <h2 className={s.summaryBlockTitle}>Внимание</h2>
          <ul className={s.summaryBirthdayList}>
            {notifications.slice(0, 5).map((n) => (
              <li key={n.id}>
                {n.action_path ? (
                  <Link to={n.action_path}>{n.text}</Link>
                ) : (
                  n.text
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className={s.summaryBlock}>
        <div className={s.summaryBlockHead}>
          <h2 className={s.summaryBlockTitle}>Ближайшие занятия</h2>
          <Link to={staffScheduleUrl({ view: "day", date: today })} className={s.summaryBlockMeta}>
            Календарь →
          </Link>
        </div>
        {nearestList.length === 0 ? (
          <p className={s.summaryMuted}>На ближайшее время занятий нет</p>
        ) : (
          <ul className={s.summarySessionList}>
            {nearestList.slice(0, 6).map((sess) => {
              const expected = sess.expected_count ?? sess.group?.member_count ?? 0;
              const present = sess.present_count ?? 0;
              const pct = sessionProgress(present, expected);
              return (
                <li key={sess.id}>
                  <button
                    type="button"
                    className={s.summarySessionCard}
                    onClick={() => openJournal(sess)}
                  >
                    <div className={s.summarySessionTop}>
                      <strong>{sess.group?.name || sess.title || "Занятие"}</strong>
                      <span>{formatTimeRu(sess.start_datetime)}</span>
                    </div>
                    <div className={s.summarySessionMeta}>
                      <span>
                        {present}/{expected || "—"} на месте
                      </span>
                      {expected > 0 ? <span>{pct}%</span> : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className={s.summaryQuick}>
        <button
          type="button"
          className={s.summaryQuickBtn}
          onClick={() => navigate(staffJournalUrl({ date: today }))}
        >
          <IconJournal width={20} height={20} />
          Журнал
        </button>
        <button
          type="button"
          className={s.summaryQuickBtn}
          onClick={() => navigate(staffScheduleUrl({ view: "month", date: today }))}
        >
          <IconCalendar width={20} height={20} />
          Расписание
        </button>
        <Link to={hubClientsPath("/staff/app")} className={s.summaryQuickBtn}>
          <IconUsers width={20} height={20} />
          Клиенты
        </Link>
      </section>

      <ClubLifeHomeSection
        title="Жизнь клуба"
        limit={2}
        mode="admin"
        feedLink="/staff/app/life"
      />
    </div>
  );
}
